import { useTranslation } from "react-i18next";
import TimeInput from "../../../../../parts/inputs/time-input";
import SvgIcon from "../../../../../parts/SvgIcon";

/**
 * Per-client Morning Brief send-time override — reads/writes the
 * `coach-monitoring-settings` collection, a second backend resource
 * alongside this page's client-configuration data, not part of
 * `client-configurations` itself. See
 * msb-docs/morning-brief/morning-brief-implementation-plan.md Stage 0 & 4.
 *
 * Fully controlled by the parent: this card has no Save button of its own —
 * it shares `ClientConfiguration.tsx`'s single Save/Discard pair, which
 * detects whether this field, the rest of the config, or both changed and
 * submits accordingly.
 */
export const CoachMonitoringOverride = ({
  coachMonitoringWorkflow,
  sendTimeOverride,
  onSendTimeOverrideChange,
}: {
  coachMonitoringWorkflow?: {
    options?: {
      morningBrief?: { status?: string; allowClientOverride?: boolean };
    };
  };
  sendTimeOverride: string;
  onSendTimeOverrideChange: (value: string) => void;
}) => {
  const { t } = useTranslation("athlete-portal");

  // Morning Brief's on/off state is its own `status` (draft/published) — no
  // separate `enabled` boolean, same as every getting-started option.
  const morningBriefEnabled =
    coachMonitoringWorkflow?.options?.morningBrief?.status === "published";

  if (!morningBriefEnabled) return null;

  // The sweep and the app only use `sendTimeOverride` while client overrides
  // are allowed (it's one field shared with the client, so a stale client
  // value must not come back). Editing it here while they're off would save
  // a time nothing reads, so it's read-only until the coach turns them on.
  const overridesAllowed =
    coachMonitoringWorkflow?.options?.morningBrief?.allowClientOverride ===
    true;

  return (
    <div className="client-config-card">
      <div className="client-config-card__left">
        <legend className="client-config-template-bar__label">
          <SvgIcon
            classNames="shrink-0"
            src="/img/new-icons/vuesax-linear-clock.svg"
          />
          <span className="p4 font-semibold">
            {t("clientConfiguration.coachMonitoring.title")}
          </span>
        </legend>
        <span>
          {overridesAllowed
            ? t("clientConfiguration.coachMonitoring.description")
            : t("clientConfiguration.coachMonitoring.overridesOffDescription")}
        </span>
      </div>
      <div className="client-config-card__right">
        <TimeInput
          name="morningBriefSendTimeOverride"
          value={sendTimeOverride}
          onChange={onSendTimeOverrideChange}
          disabled={!overridesAllowed}
        />
      </div>
    </div>
  );
};
